import React, { useEffect, useRef, useState } from "react";
import Card from "components/card";

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { IoEye } from "react-icons/io5";
import { useNavigate, useSearchParams } from "react-router-dom";
import UserService from "services/user.service";
import Loader from "../../../assets/img/loader.gif";
import Toast from "components/toaster";
import DateRangePicker from "components/fields/DateRangePicker";
import moment from "moment";
import { FiSearch } from "react-icons/fi";
import Modal from "components/modal/Modal";
import { BsChevronDown } from "react-icons/bs";
import GooglePagination from "components/pagination/GooglePagination";

const columnHelper = createColumnHelper();

// Module-level tabCache for persistent data across component mounts/unmounts
const tabCache = {
  users: {
    data: null,
    firstLoad: true,
  },
};

const Users = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dropdownRef = useRef();
  const profileRef = useRef();
  const activateRef = useRef();
  const [sorting, setSorting] = React.useState([]);
  // Initialize with cached data if available to prevent "No data found" flash
  const [data, setData] = useState(() => {
    const cache = tabCache.users;
    return cache?.data?.data || [];
  });
  const [isLoading, setIsLoading] = useState(() => {
    const cache = tabCache.users;
    // Only show loading if no cache exists
    return cache?.firstLoad && !cache?.data;
  });
  const [popupOpen, setPopupOpen] = useState(false);
  const [profilePopup, setProfilePopup] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isViewLoading, setIsViewLoading] = useState(false);
  const [id, setId] = useState("");
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [confirmLoader, setConfirmLoader] = useState(false);
  
  // Initialize date filter from query parameters
  const [value, setValue] = useState(() => {
    const startDateParam = searchParams.get("startDate");
    const endDateParam = searchParams.get("endDate");
    return {
      startDate: startDateParam ? new Date(startDateParam) : null,
      endDate: endDateParam ? new Date(endDateParam) : null,
    };
  });
  const [searchText, setSearchText] = useState("");
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  // Initialize pagination with cached data if available
  const [totalPages, setTotalPages] = useState(() => {
    const cache = tabCache.users;
    if (cache?.data?.totalCount) {
      return Math.ceil(cache.data.totalCount / 20);
    }
    return 1;
  });
  const [totalCount, setTotalCount] = useState(() => {
    const cache = tabCache.users;
    return cache?.data?.totalCount || 0;
  });

  const getUser = async (showLoader = true) => {
    if (showLoader) {
      setIsLoading(true);
    }
    try {
      const body = {
        pagination: {
          page: currentPage,
          pageSize: pageSize,
          projection: {},
          filters: {
            startDate: value.startDate
              ? moment(value.startDate).startOf("day").format()
              : null,
            endDate: value.endDate
              ? moment(value.endDate).endOf("day").format()
              : null,
          },
          sort: {},
        },
        searchtext: searchText,
      };
      const response = await UserService.getUsers(body);
      const responseData = response.data.data;
      const responseTotalCount = response.data.totalCount || 0;
      
      // Update cache
      tabCache.users.data = {
        data: responseData,
        totalCount: responseTotalCount,
      };
      
      // Set firstLoad to false after successful fetch
      if (tabCache.users.firstLoad) {
        tabCache.users.firstLoad = false;
      }
      
      setData(responseData);
      setTotalCount(responseTotalCount);
      setTotalPages(Math.ceil(responseTotalCount / pageSize));
    } catch (error) {
      Toast("error", error.response?.data?.message || "Failed to fetch users");
    } finally {
      if (showLoader) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    // Check cache first
    const cache = tabCache.users;
    
    // If cached data exists, show it instantly
    if (cache.data) {
      setData(cache.data.data || []);
      setTotalCount(cache.data.totalCount || 0);
      setTotalPages(Math.ceil((cache.data.totalCount || 0) / pageSize));
    }
    
    // Always fetch fresh data in background
    // Show loader only on firstLoad (when no cache exists yet)
    const shouldShowLoader = cache.firstLoad && !cache.data;
    getUser(shouldShowLoader);
  }, [currentPage, pageSize, searchText, value.startDate, value.endDate]);

  // Update query parameters when date filter changes (but not on initial mount from URL)
  useEffect(() => {
    const currentStartDate = searchParams.get("startDate");
    const currentEndDate = searchParams.get("endDate");
    const newStartDate = value.startDate ? moment(value.startDate).format("YYYY-MM-DD") : null;
    const newEndDate = value.endDate ? moment(value.endDate).format("YYYY-MM-DD") : null;
    
    // Only update if the values actually changed (avoid updating on initial mount)
    if (currentStartDate !== newStartDate || currentEndDate !== newEndDate) {
      const params = new URLSearchParams(searchParams);
      if (newStartDate) {
        params.set("startDate", newStartDate);
      } else {
        params.delete("startDate");
      }
      if (newEndDate) {
        params.set("endDate", newEndDate);
      } else {
        params.delete("endDate");
      }
      setSearchParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.startDate, value.endDate]);

  const handleSearch = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo(0, 0); // Scroll to top when changing pages
  };

  const handleOpen = async (item) => {
    setIsViewLoading(true);
    setId(item);
    try {
      const body = {
        id: item,
      };
      const response = await UserService.getUserDetails(body);
      setProfilePopup(true);
      document.body.style.overflow = "hidden";
    } catch (error) {
      Toast("error", error.response.data.message);
    } finally {
      setIsViewLoading(false);
      setId("");
    }
  };

  const handleOpenAvtive = async (id, active) => {
    setSelectedUser(active.original.is_active);
    setId(id);
    setPopupOpen(true);
    document.body.style.overflow = "hidden";
  };

  const handleStatusChange = async (rowId, newStatus) => {
    setIsViewLoading(true);
    try {
      const body = {
        user_id: rowId,
        is_active: newStatus,
      };
      const response = await UserService.activationUser(body);
      Toast("success", response.data.message);
      getUser(false);
    } catch (error) {
      Toast(
        "error",
        error.response?.data?.message || "Failed to update status"
      );
    } finally {
      setActiveDropdown(null);
      setIsViewLoading(false);
    }
  };

  const handleToggleActivation = async () => {
    setConfirmLoader(true);
    try {
      const body = {
        user_id: id,
        is_active: !selectedUser,
      };
      const response = await UserService.activationUser(body);
      setPopupOpen(false);
      document.body.style.overflow = "unset";
      Toast("success", response.data.message);
      getUser(false);
    } catch (error) {
      Toast("error", error.response.data.message);
    } finally {
      setConfirmLoader(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfilePopup(false);
        document.body.style.overflow = "unset";
      }
      if (activateRef.current && !activateRef.current.contains(event.target)) {
        setPopupOpen(false);
        document.body.style.overflow = "unset";
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const columns = [
    columnHelper.accessor("full_name", {
      id: "full_name",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Full Name</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-white">
          {info.getValue() ? info.getValue() : "-"}
        </p>
      ),
    }),
    columnHelper.accessor("phone", {
      id: "phone",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Mobile Number</p>
      ),
      cell: (info) => {
        const value = info.getValue();
        return (
          <div className="flex items-center">
            <p className="text-sm font-bold text-white">
              {value && value !== "null" ? value : "--"}
            </p>
          </div>
        );
      },
    }),
    columnHelper.accessor("is_verified", {
      id: "is_verified",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Verified</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-white">
          {info.getValue() ? "Yes" : "No"}
        </p>
      ),
    }),
    columnHelper.accessor("email", {
      id: "email",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Email</p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          {info.getValue() ? info.getValue() : "--"}
        </div>
      ),
    }),
    // Referred By (User ID)
// Referral Code
// columnHelper.accessor("referral_code", {
//   id: "referral_code",
//   header: () => (
//     <p className="text-sm font-bold uppercase text-white">
//       Referral Code
//     </p>
//   ),
//   cell: (info) => (
//     <p className="text-sm font-bold text-white">
//       {info.getValue() || "--"}
//     </p>
//   ),
// }),

// Referred By
// columnHelper.accessor("referred_by.name", {
//   id: "referred_by",
//   header: () => (
//     <p className="text-sm font-bold uppercase text-white">
//       Referred By
//     </p>
//   ),
//   cell: (info) => (
//     <p className="text-sm font-bold text-white">
//       {info.getValue() || "--"}
//     </p>
//   ),
// }),


    columnHelper.accessor("status", {
      id: "status",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Status</p>
      ),
      cell: (info) => {
        const rowId = info.row.original._id;
        const status = info.row.original?.is_active;

        return (
          <div className="relative">
            <div
              data-dropdown="status"
              className={`ms-center m-0 flex w-28 cursor-pointer items-center justify-between rounded-lg ${
                status ? "bg-green-500/20" : ""
              } px-3 py-1.5 text-sm font-bold ${
                status ? "text-green-500" : ""
              }`}
              style={!status ? {
                backgroundColor: "rgba(255,37,37,0.1)",
                color: "#f06670d5"
              } : {}}
              onClick={() =>
                setActiveDropdown(activeDropdown === rowId ? null : rowId)
              }
            >
              <span>{!status ? "In Active" : "Active"}</span>
              <BsChevronDown 
                className={`h-3 w-3 ${status ? "text-green-500" : ""}`}
                style={!status ? {
                  stroke: "#f06670d5",
                  strokeWidth: "1",
                  fill: "none"
                } : {
                  stroke: "currentColor",
                  strokeWidth: "1",
                  fill: "none"
                }}
              />
            </div>
            {activeDropdown === rowId && (
              <div
                className="absolute left-0 z-50 mt-1 w-28 rounded-md bg-[#0F1735] border border-gray-600 px-1 py-1 shadow-lg"
                ref={dropdownRef}
              >
                {status ? (
                  // If Active, show only In Active option
                  <button
                    className="block w-full rounded-md px-2.5 py-1 text-left text-sm font-bold text-white transition-colors hover:bg-gray-600"
                    onClick={() => {
                      handleStatusChange(rowId, false);
                    }}
                  >
                    In Active
                  </button>
                ) : (
                  // If In Active, show only Active option
                  <button
                    className="block w-full rounded-md px-2.5 py-1 text-left text-sm font-bold text-white transition-colors hover:bg-gray-600"
                    onClick={() => {
                      handleStatusChange(rowId, true);
                    }}
                  >
                    Active
                  </button>
                )}
              </div>
            )}
          </div>
        );
      },
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  return (
    <div className="mt-3 grid h-full grid-cols-1">
      {/* Container wrapper */}
      <div className="col-span-1 w-full xl:col-span-1 min-w-0">
        <div className="flex flex-col sm:flex-row sm:justify-between gap-3 mb-4">
          <div className="mt-2 md:mt-4 flex h-fit justify-start">
            <div className="flex items-center rounded-full bg-navy-800 py-2 h-[40px] text-white w-full sm:w-auto xl:w-[170px]">
              <p className="pl-3 pr-2 text-xl">
                <FiSearch className="h-4 w-4 text-white" />
              </p>
              <input
                type="text"
                placeholder="Search..."
                className="block flex-1 sm:w-[90px] sm:max-w-[90px] bg-navy-800 text-sm font-medium text-white outline-none placeholder:!text-white"
                onChange={handleSearch}
              />
            </div>
          </div>
          <div className="mt-2 md:mt-4 flex h-fit w-full sm:w-[280px] justify-start sm:justify-end">
            <DateRangePicker
              value={value}
              onChange={(newValue) => setValue(newValue)}
              showShortcuts={true}
              classNames="w-full sm:w-fit"
            />
          </div>
        </div>
        <Card extra={"w-full pb-6 relative"}>
          <div className="mt-2 mx-2">
            <div className="overflow-x-auto !p-0 !m-0 !pr-0">
              <table className="table-fixed w-full border-collapse !mr-0 !pr-0 !border-r-0" style={{ tableLayout: 'fixed', width: '100%' }}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className="!border-px !border-gray-400"
                  >
                    {headerGroup.headers.map((header, index) => {
                      const isFirst = index === 0;
                      const isLast = index === headerGroup.headers.length - 1;
                      const columnId = header.column.id;
                      
                      // Set fixed widths for all columns to prevent layout shift
                      let columnWidth = 'auto';
                      if (columnId === 'full_name') columnWidth = '200px';
                      else if (columnId === 'phone') columnWidth = '200px';
                      else if (columnId === 'is_verified') columnWidth = '180px';
                      else if (columnId === 'email') columnWidth = '200px';
                      else if (columnId === 'status') columnWidth = '200px';
                      // else if (columnId === 'referral_code') columnWidth = '160px';
                      // else if (columnId === 'referred_by') columnWidth = '180px';

                      
                      return (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          onClick={header.column.getToggleSortingHandler()}
                          className={`cursor-pointer border-b-[1px] border-gray-500/10 pb-2 pt-4 text-start ${isFirst ? 'pl-6' : 'pl-4'} ${isLast ? '' : 'pr-4'}`}
                          style={{ width: columnWidth }}
                        >
                          <div className="items-center justify-between text-xs text-gray-200">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: "",
                              desc: "",
                            }[header.column.getIsSorted()] ?? null}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {isLoading && tabCache.users?.firstLoad && !tabCache.users?.data ? (
                  <tr>
                    <td colSpan={columns.length} className="h-[600px] align-middle">
                      <div className="flex h-full items-center justify-center">
                        <img
                          src={Loader}
                          alt="Loading..."
                          className="h-16 w-16"
                        />
                      </div>
                    </td>
                  </tr>
                ) : table.getRowModel().rows?.length === 0 && !isLoading ? (
                  <tr>
                    <td colSpan={columns.length} className="py-12 align-middle">
                      <div className="flex items-center justify-center">
                        <p className="text-white">No data found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <>
                    {table.getRowModel().rows.map((row, rowIndex) => {
                      const isLastRow = rowIndex === table.getRowModel().rows.length - 1;
                      return (
                        <tr key={row.id} className={isLastRow ? "" : "border-b-[1px] border-gray-500/10"}>
                          {row.getVisibleCells().map((cell, cellIndex) => {
                            const isFirst = cellIndex === 0;
                            const isLast = cellIndex === row.getVisibleCells().length - 1;
                            const columnId = cell.column.id;
                            
                            // Set fixed widths matching header widths exactly
                            let columnWidth = 'auto';
                            if (columnId === 'full_name') columnWidth = '200px';
                            else if (columnId === 'phone') columnWidth = '200px';
                            else if (columnId === 'is_verified') columnWidth = '120px';
                            else if (columnId === 'email') columnWidth = '200px';
                            else if (columnId === 'status') columnWidth = '200px';
                            // else if (columnId === 'referral_code') columnWidth = '160px';
                            // else if (columnId === 'referred_by') columnWidth = '180px';

                            
                            return (
                              <td
                                key={cell.id}
                                className={`border-white/0 py-3 ${isFirst ? 'pl-6' : 'pl-4'} ${isLast ? '' : 'pr-4'}`}
                                style={{ width: columnWidth }}
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </>
                )}
              </tbody>
            </table>
            </div>
          </div>

          {/* Google-style Pagination */}
          {!isLoading && table.getRowModel().rows.length > 0 && totalPages > 1 && (
            <div className="mt-3 pt-3 px-6">
              <GooglePagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                className="w-full"
              />
            </div>
          )}
        </Card>

        <Modal
          isOpen={popupOpen}
          onClose={() => {
            setPopupOpen(false);
            document.body.style.overflow = "unset";
          }}
          isActive={selectedUser}
          onToggle={handleToggleActivation}
          activateRef={activateRef}
          confirmLoader={confirmLoader}
        />
      </div>
    </div>
  );
};

export default Users;
